import React from "react";

import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import { NotificationBanner } from "@src/components/notification_banner";
import { NOTIFICATION_TYPE } from "@src/constants";
import { setActiveTabKey } from "@src/services/requestOverviewSlice";
import {
  useAcceptUpdatedRequestMutation,
  useGetTeamLeadRequestDetailsQuery,
  useRevokeUpdatedRequestMutation,
} from "@src/services/slices/teamLeadsApi";
import { useAppDispatch } from "@src/setupStore";
import { AlertConfig } from "@src/types";
import {
  createAlertConfig,
  getStaffingRequestAlertConfig,
} from "@src/utils/notification_utils";
import { Button, message, Space, Tooltip } from "antd";
import {
  checkIsProposedRequestUpdated,
  checkIsDeclinedRequestUpdated,
} from "@src/features/staffing_request_details/utils/staffing_request_details_utils";

interface StaffingRequestNotificationBarProps {
  requestId: number;
}

const StaffingRequestNotificationBar: React.FC<
  StaffingRequestNotificationBarProps
> = ({ requestId }) => {
  const dispatch = useAppDispatch();
  const { data: request } = useGetTeamLeadRequestDetailsQuery(requestId, {
    skip: !requestId,
  });

  const [acceptUpdatedRequest] = useAcceptUpdatedRequestMutation();
  const [revokeUpdatedRequest] = useRevokeUpdatedRequestMutation();

  const isProposedRequestUpdated: boolean =
    checkIsProposedRequestUpdated(request);
  const isDeclinedRequestUpdated: boolean =
    checkIsDeclinedRequestUpdated(request);
  const isFreeTextFieldChanged: boolean = request?.isOnlyFreeTextFieldChanged;

  const alertConfigs: AlertConfig[] = [
    ...getStaffingRequestAlertConfig(request),
    createProposalAlertConfig(),
    createAutoAcceptAlertConfig(),
  ];

  /**
   * Function to handle the request still applies. When a project lead updates a proposed request than the
   * team lead has to decide if the changed values still applies.
   */
  async function handleAcceptRequest() {
    try {
      await acceptUpdatedRequest({
        roleRequestId: request.projectRoleRequestId,
      }).unwrap();
      // Reset the active tab to the overview page
      dispatch(setActiveTabKey("0"));
    } catch (error) {
      console.error("Failed to accept the request", error);
    }
  }

  /**
   * Function to handle the request does not apply anymore. When a project lead updates a proposed request than the
   * team lead has to decide if the assigned employee to the new changed values still applies.
   */
  async function handleRevokeRequest() {
    try {
      await revokeUpdatedRequest({
        roleRequestId: request.projectRoleRequestId,
      }).unwrap();
      message.success("Request update revoked and set to requested state");
    } catch (error) {
      console.error("Failed to revoke the request", error);
    }
  }

  /**
   * Function to create an alert configuration object for the proposal alert.
   */
  function createProposalAlertConfig(): AlertConfig | null {
    const actionButtons: React.ReactNode = (
      <Space>
        <Tooltip
          placement="top"
          title="The new requested allocations will be confirmed"
        >
          <Button
            type="primary"
            size="small"
            icon={<CheckOutlined />}
            onClick={handleAcceptRequest}
          >
            YES
          </Button>
        </Tooltip>
        <Tooltip
          placement="top"
          title="The request would be changed to 'Requested'"
        >
          <Button
            danger
            type="primary"
            size="small"
            icon={<CloseOutlined />}
            onClick={handleRevokeRequest}
          >
            NO
          </Button>
        </Tooltip>
      </Space>
    );

    const alertTitle = isProposedRequestUpdated
      ? "Project lead changed marked allocations"
      : "The proposal was updated after being declined";

    const alertDescription = isProposedRequestUpdated
      ? `Does your Proposal '${request?.assignedTeamMember}' still apply?`
      : `Does your proposal still apply?`;

    return createAlertConfig(
      isProposedRequestUpdated || isDeclinedRequestUpdated,
      alertTitle,
      alertDescription,
      NOTIFICATION_TYPE.WARNING,
      actionButtons
    );
  }

  /**
   * Creates an alert configuration object for the auto accept alert.
   */
  function createAutoAcceptAlertConfig(): AlertConfig | null {
    return createAlertConfig(
      isFreeTextFieldChanged,
      "REQUEST CHANGED",
      "The update has been automatically accepted.",
      NOTIFICATION_TYPE.INFO
    );
  }

  return (
    <div style={{ width: "100%" }}>
      {alertConfigs
        .filter((alert: AlertConfig) => alert?.condition)
        .map((shownAlert: AlertConfig) => (
          <NotificationBanner
            type={shownAlert.type}
            message={shownAlert.message}
            action={shownAlert.action}
          />
        ))}
    </div>
  );
};

export default StaffingRequestNotificationBar;
